import { ModelBase } from '../../core/model/model-base';
import { Property } from '../../rent/model/property';
import { ErrandStatus, ErrandStatusToString, ErrandType, ErrandTypeToString } from './const/enums';
import { ExecutorGroup } from './executor-group';
import { WebUser } from '../../common/model/webUser';
import { Building } from '../../rent/model/building';
import { ErrandTagSelection } from '../../standalone-errand/model/errand-tag';
import { TenantErrandType, TenantErrandTypeToString } from '../../standalone-errand/model/errand-category';
import { FaultNotificationPriority, FaultNotificationPriorityToString } from '../../fault-notification/model/const/enum';
import { LocalDate } from '../../core/model/local-date';

export interface ErrandRelatedRow {
  rentalObjectCaption?: string;
  apartmentNumber?: string;
  rentalObjectType?: string;
  floor?: number;
  contractHolder?: string;
  contractHolderEmail?: string;
  contractHolderPhone?: string;
  leaseAgreementPopularName?: string;
}

export class Errand extends ModelBase {
  constructor(dataContext?: Errand) {
    super();
    if (dataContext) {
      this.errandNumber = dataContext.errandNumber;
      this.name = dataContext.name;
      this.description = dataContext.description;
      this.status = dataContext.status;
      this.type = dataContext.type;
      this.property = dataContext.property;
      this.webUser = dataContext.webUser;
      this.executorGroup = dataContext.executorGroup;
      this.resource = dataContext.resource;
      this.tag = dataContext.tag;
      this.category = dataContext.category;
      this.subCategory = dataContext.subCategory;
      this.tenantErrandType = dataContext.tenantErrandType;
      this.tenantErrandTypeName = dataContext.tenantErrandTypeName;
      this.dwelling = dataContext.dwelling;
      this.unreadDialogMessagesCount = dataContext.unreadDialogMessagesCount ?? 0;
      this.priority = dataContext.priority;
      this.createdTime = dataContext.createdTime;
      this.completedTime = dataContext.completedTime;
      this.creatorName = dataContext.creatorName;
      this.creatorUsername = dataContext.creatorUsername;
      this.relatedRows = dataContext.relatedRows || [];
      this.internalTimeSum = dataContext.internalTimeSum;
      this.bookedDate = dataContext.bookedDate;
      this.bookedTime = dataContext.bookedTime;
      this.buildings = dataContext.buildings?.map(x => new Building(x)) || [];
      this.checklists = dataContext.checklists?.map(x => new ErrandChecklist(x)) || [];
    }
  }

  errandNumber: string;
  name: string;
  dwelling: string;
  description: string;
  status: ErrandStatus;
  type: ErrandType;
  property: Property;
  buildings: Building[];
  webUser: WebUser;
  executorGroup: ExecutorGroup;
  resource: WebUser;
  tag: ErrandTagSelection;
  category: string;
  subCategory: string;
  tenantErrandType?: TenantErrandType;
  tenantErrandTypeName: string;
  checklists: ErrandChecklist[];
  unreadDialogMessagesCount: number = 0;
  priority: FaultNotificationPriority;
  createdTime: Date;
  completedTime: Date;
  creatorName: string;
  creatorUsername: string;
  relatedRows: ErrandRelatedRow[] = [];
  internalTimeSum: number;
  bookedDate: LocalDate;
  bookedTime: Date | string;

  get priorityCaption(): string {
    if (
      this.priority === null ||
      this.priority === undefined ||
      !Number.isInteger(this.priority) ||
      this.priority < FaultNotificationPriority.NotSet ||
      this.priority > FaultNotificationPriority.Highest
    ) {
      return '';
    }

    return FaultNotificationPriorityToString(this.priority);
  }

  private relatedValues(field: keyof ErrandRelatedRow): string[] {
    const values = this.relatedRows.map(row => {
      const value = row[field];
      return value === null || value === undefined ? '' : String(value);
    });
    return values.some(value => value !== '') ? values : [];
  }

  get apartmentNumberLines(): string[] { return this.relatedValues('apartmentNumber'); }
  get dwellingLines(): string[] { return this.relatedValues('rentalObjectCaption'); }
  get rentalObjectTypeLines(): string[] { return this.relatedValues('rentalObjectType'); }
  get floorLines(): string[] { return this.relatedValues('floor'); }
  get contractHolderLines(): string[] { return this.relatedValues('contractHolder'); }
  get contractHolderEmailLines(): string[] { return this.relatedValues('contractHolderEmail'); }
  get contractHolderPhoneLines(): string[] { return this.relatedValues('contractHolderPhone'); }
  get leaseAgreementPopularNameLines(): string[] { return this.relatedValues('leaseAgreementPopularName'); }

  get bookedTimeCaption(): string {
    if (!this.bookedTime) return '';
    if (this.bookedTime instanceof Date) {
      return new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(this.bookedTime);
    }
    // Booking is a wall-clock time; preserve stored midnight even if the API
    // serialized its DateTime with a timezone suffix.
    const timePart = this.bookedTime.match(/(?:T|^)(\d{2}:\d{2})/);
    if (timePart) return timePart[1];
    const parsed = new Date(this.bookedTime);
    return Number.isNaN(parsed.getTime())
      ? this.bookedTime
      : new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(parsed);
  }

  get errandStatus() {
    return this.status ? ErrandStatusToString(this.status) : '';
  }

  get errandType() {
    return this.type ? ErrandTypeToString(this.type) : '';
  }

  get tenantErrandTypeTranslation() {
    return this.tenantErrandType !== null && this.tenantErrandType !== undefined
      ? TenantErrandTypeToString(this.tenantErrandType)
      : '';
  }

  get errandWebUserName() {
    return this.webUser?.personName || this.webUser?.name || '';
  }

  get errandExecutorGroupName() {
    return this.executorGroup?.name || '';
  }

  get errandResourceName() {
    return this.resource?.personName || this.resource?.name || '';
  }

  get errandTagName() {
    return this.tag?.name || '';
  }

  get propertyName() {
    if (!this.property) {
      return '';
    }

    return this.property.propertyNumber
      ? `${this.property.propertyNumber} ${this.property.propertyName}`
      : this.property.propertyName || '';
  }

  get propertyCostPlace(): string {
    return this.property?.costPlace || '';
  }

  get companyName() {
    return this.property?.companyName || '';
  }
  
  get totalChecklistRowsCount(): number {
    return this.checklists?.length;
  }

  get completedChecklistRowsCount(): number {
    return this.checklists?.filter(cl => cl.isCompleted)?.length;
  }

  get checklistRowsProgressText() {
    if (this.totalChecklistRowsCount && this.completedChecklistRowsCount != null) {
      return `${this.completedChecklistRowsCount} / ${this.totalChecklistRowsCount}`;
    }

    return '';
  }

  get buildingNames(): string {
    return this.buildings?.map(x => `${x.number} ${x.name}`).join(',');
  }

  get buildingNameLines(): string[] {
    return this.buildings?.map(x => `${x.number} ${x.name}`) || [];
  }

  get checklistsNames(): string[] {
    return this.checklists?.map(cl => cl.name);
  }

  get checklistsCount(): number {
    return this.checklists?.length;
  }

  toString(): string {
    return this.name;
  }
}

export class ErrandChecklist extends ModelBase {
  constructor(dataContext?: ErrandChecklist) {
      super();
      if (dataContext) {
        this.id = dataContext.id;
        this.name = dataContext.name;
        this.note = dataContext.note;
        this.isCompleted = dataContext.isCompleted;
        this.completedTime = dataContext.completedTime;
        this.completedByWebUser = dataContext.completedByWebUser;
        this.checklistRows = dataContext.checklistRows.map(x => new ErrandChecklistRow(x));
      }
    }
  
    name: string;
    note: string;
    isCompleted: boolean;
    completedTime?: Date;
    completedByWebUser?: WebUser;
    checklistRows: ErrandChecklistRow[];
}

export class ErrandChecklistRow extends ModelBase {
  constructor(dataContext?: ErrandChecklistRow) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.text = dataContext.text;
      this.order = dataContext.order;
      this.isCompleted = dataContext.isCompleted;
      this.completedTime = dataContext.completedTime;
      this.completedByWebUser = dataContext.completedByWebUser;
    }
  }

  text: string;
  order: number;
  isCompleted: boolean;
  completedTime?: Date;
  completedByWebUser?: WebUser;
  note: string;
}
